import React, { useState, useEffect } from 'react';
import { Check, Inbox, Calendar, Star, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { toast } from 'sonner';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [showPersonalOnly, setShowPersonalOnly] = useState(false);

    useEffect(() => {
        fetchNotifications();
        const cleanup = setupSubscription();
        return () => {
            cleanup.then(unsubscribe => unsubscribe && unsubscribe());
        };
    }, []);

    const fetchNotifications = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;

            setNotifications(data || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const setupSubscription = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const subscription = supabase
            .channel('notifications_page_channel')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    setNotifications(prev => [payload.new, ...prev]);
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    setNotifications(prev => prev.map(n => n.id === payload.new.id ? payload.new : n));
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const markAsRead = async (id) => {
        try {
            const { error } = await supabase
                .from('notifications')
                .update({ is_read: true })
                .eq('id', id);

            if (error) throw error;

            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { error } = await supabase
                .from('notifications')
                .update({ is_read: true })
                .eq('user_id', user.id)
                .eq('is_read', false);

            if (error) throw error;

            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            toast.success('All notifications marked as read');
        } catch (error) {
            console.error('Error marking all as read:', error);
            toast.error('Failed to update notifications');
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }

        if (notification.type === 'task') {
            navigate('/tasks', { state: { openProblemId: notification.reference_id } });
        } else if (notification.type === 'experiment') {
            navigate(notification.link, { state: { openExperimentId: notification.reference_id } });
        } else if (notification.type === 'team_chat') {
            navigate('/team-chat', { state: { selectedChannelId: notification.reference_id } });
        } else if (notification.link) {
            navigate(notification.link);
        }
    };

    const groupNotifications = (notifs) => {
        const groups = {
            Today: [],
            Yesterday: [],
            Older: []
        };

        const filtered = showPersonalOnly
            ? notifs.filter(n => n.type === 'team_chat')
            : notifs;

        filtered.forEach(n => {
            const date = new Date(n.created_at);
            if (isToday(date)) {
                groups.Today.push(n);
            } else if (isYesterday(date)) {
                groups.Yesterday.push(n);
            } else {
                groups.Older.push(n);
            }
        });

        return groups;
    };

    const groupedNotifications = groupNotifications(notifications);
    const hasNotifications = Object.values(groupedNotifications).some(group => group.length > 0);

    const getIconColor = (type) => {
        switch (type) {
            case 'task': return 'bg-blue-400';
            case 'project': return 'bg-indigo-400';
            case 'experiment': return 'bg-teal-400';
            case 'document': return 'bg-purple-400';
            default: return 'bg-gray-400';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="loading-dots mb-4">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <p className="text-gray-400">Loading notifications...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold text-white">Notifications</h1>
                        <button
                            onClick={() => setShowPersonalOnly(!showPersonalOnly)}
                            className={`p-1.5 rounded-lg transition-all duration-200 border ${showPersonalOnly
                                ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20 shadow-[0_0_10px_rgba(234,179,8,0.1)]'
                                : 'text-gray-400 border-transparent hover:bg-white/5 hover:text-white'
                                }`}
                            title={showPersonalOnly ? "Show All" : "Show Personal Only"}
                        >
                            <Star size={20} className={showPersonalOnly ? "fill-yellow-400" : ""} />
                        </button>
                        <button
                            onClick={markAllAsRead}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-all duration-200"
                            title="Mark all as read"
                        >
                            <CheckCheck size={20} />
                        </button>
                    </div>
                    <p className="text-gray-400">Stay updated with the latest activities.</p>
                </div>
            </div>

            <div className="space-y-8">
                {Object.entries(groupedNotifications).map(([group, items]) => (
                    items.length > 0 && (
                        <div key={group}>
                            <h2 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
                                <Calendar size={16} />
                                {group}
                            </h2>
                            <div className="space-y-3">
                                {items.map(notification => (
                                    <div
                                        key={notification.id}
                                        className={`glass-card p-4 flex items-start gap-4 cursor-pointer transition-all duration-200 hover:scale-[1.01] ${!notification.is_read ? 'border-l-4 border-l-blue-500 bg-blue-500/5' : 'opacity-75 hover:opacity-100'
                                            }`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        {notification.type === 'team_chat' ? (
                                            <Star size={18} className="mt-1 text-yellow-400 fill-yellow-400 shrink-0" />
                                        ) : (
                                            <div className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${getIconColor(notification.type)}`} />
                                        )}

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-4">
                                                <h3 className={`text-base font-medium ${!notification.is_read ? 'text-white' : 'text-gray-300'}`}>
                                                    {notification.title}
                                                </h3>
                                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                                        </div>

                                        {!notification.is_read && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsRead(notification.id);
                                                }}
                                                className="p-2 text-gray-500 hover:text-green-400 hover:bg-white/5 rounded-lg transition-colors"
                                                title="Mark as read"
                                            >
                                                <Check size={18} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                ))}

                {!hasNotifications && (
                    <div className="text-center py-20">
                        {showPersonalOnly ? (
                            <>
                                <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Star size={40} className="text-yellow-400 fill-yellow-400/20" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">No personal notifications</h3>
                                <p className="text-gray-400">You haven't received any personal mentions or invites yet.</p>
                            </>
                        ) : (
                            <>
                                <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Inbox size={40} className="text-gray-500" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">All caught up!</h3>
                                <p className="text-gray-400">You have no notifications at the moment.</p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
